import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export type TemplateCategory =
  | 'invoice'
  | 'credit_note'
  | 'debit_note'
  | 'quote'
  | 'statement'
  | 'report'
  | 'letter'
  | 'receipt'
  | 'write_off'
  | 'advance';

export type PageSize = 'A4' | 'A3' | 'Letter' | 'Legal';
export type Orientation = 'portrait' | 'landscape';
export type SectionType = 'header' | 'body' | 'footer' | 'watermark';

export interface TemplateVariable {
  key: string;
  label: string;
  type: string;
  description?: string;
  example?: unknown;
  required?: boolean;
  source?: string;
}

export interface DocumentTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  category: TemplateCategory;
  practice_entity_id: string | null;
  page_size: PageSize;
  orientation: Orientation;
  margin_top: number;
  margin_right: number;
  margin_bottom: number;
  margin_left: number;
  header_html: string | null;
  body_html: string;
  footer_html: string | null;
  css: string | null;
  variables: TemplateVariable[];
  sample_data: Record<string, unknown> | null;
  /** For report templates: the report data source (e.g. 'billing', 'jobs'). */
  report_category: string | null;
  /** For report templates: an optional report-type label. */
  report_type: string | null;
  /** For report templates: the saved report this template is bound to. */
  report_saved_report_id: string | null;
  is_default: boolean;
  is_active: boolean;
  version: number;
  created_by_id: string | null;
  updated_by_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentTemplateSection {
  id: string;
  template_id: string;
  name: string;
  section_type: SectionType;
  html_content: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DocumentTemplateFull extends DocumentTemplate {
  sections: DocumentTemplateSection[];
}

export interface GeneratedDocument {
  id: string;
  template_id: string | null;
  document_type: string;
  file_name: string;
  file_size_bytes: number | null;
  mime_type: string;
  s3_key: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  invoice_id: string | null;
  client_id: string | null;
  job_id: string | null;
  error_message: string | null;
  generated_at: string | null;
  created_at: string;
}

export interface CreateTemplateInput {
  name: string;
  description?: string | null;
  category: TemplateCategory;
  /** Required for billing/document templates; null for report templates. */
  practice_entity_id: string | null;
  page_size?: PageSize;
  orientation?: Orientation;
  margin_top?: number;
  margin_right?: number;
  margin_bottom?: number;
  margin_left?: number;
  header_html?: string | null;
  body_html: string;
  footer_html?: string | null;
  css?: string | null;
  variables?: TemplateVariable[];
  sample_data?: Record<string, unknown> | null;
  report_category?: string | null;
  report_type?: string | null;
  report_saved_report_id?: string | null;
  is_default?: boolean;
}

export interface UpdateTemplateInput {
  name?: string;
  description?: string | null;
  category?: TemplateCategory;
  practice_entity_id?: string | null;
  page_size?: PageSize;
  orientation?: Orientation;
  margin_top?: number;
  margin_right?: number;
  margin_bottom?: number;
  margin_left?: number;
  header_html?: string | null;
  body_html?: string;
  footer_html?: string | null;
  css?: string | null;
  variables?: TemplateVariable[];
  sample_data?: Record<string, unknown> | null;
  report_category?: string | null;
  report_type?: string | null;
  report_saved_report_id?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const templateKeys = {
  all: ['document-templates'] as const,
  list: (filters?: { category?: string; is_active?: string; search?: string }) =>
    [...templateKeys.all, 'list', filters] as const,
  detail: (id: string) => [...templateKeys.all, 'detail', id] as const,
  variables: (category: string) => [...templateKeys.all, 'variables', category] as const,
  generated: (filters?: Record<string, string>) =>
    [...templateKeys.all, 'generated', filters] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Lists document templates with optional filtering.
 */
export function useDocumentTemplates(filters?: {
  category?: TemplateCategory;
  practice_entity_id?: string;
  is_active?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: templateKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.category) params.set('category', filters.category);
      if (filters?.practice_entity_id) params.set('practice_entity_id', filters.practice_entity_id);
      if (filters?.is_active) params.set('is_active', filters.is_active);
      if (filters?.search) params.set('search', filters.search);

      const { data } = await apiClient.get<ApiResponse<DocumentTemplate[]>>(
        `/document-templates?${params.toString()}`,
      );
      return data.data;
    },
  });
}

/**
 * Gets a single template with sections.
 */
export function useDocumentTemplate(id: string | undefined) {
  return useQuery({
    queryKey: templateKeys.detail(id || ''),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<DocumentTemplateFull>>(
        `/document-templates/${id}`,
      );
      return data.data;
    },
    enabled: !!id,
  });
}

/**
 * Gets available variables for a template category.
 */
export function useTemplateVariables(category: string | undefined) {
  return useQuery({
    queryKey: templateKeys.variables(category || ''),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<TemplateVariable[]>>(
        `/document-templates/variables/${category}`,
      );
      return data.data;
    },
    enabled: !!category,
  });
}

/**
 * Creates a new document template.
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTemplateInput) => {
      const { data } = await apiClient.post<ApiResponse<DocumentTemplate>>(
        '/document-templates',
        input,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Updates an existing template.
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateTemplateInput & { id: string }) => {
      const { data } = await apiClient.put<ApiResponse<DocumentTemplate>>(
        `/document-templates/${id}`,
        input,
      );
      return data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(variables.id) });
    },
  });
}

/**
 * Deletes a template.
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/document-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Duplicates a template.
 */
export function useDuplicateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data } = await apiClient.post<ApiResponse<DocumentTemplate>>(
        `/document-templates/${id}/duplicate`,
        { name },
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Previews a template with data (returns rendered HTML).
 */
export function usePreviewTemplate() {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data?: Record<string, unknown> }) => {
      const response = await apiClient.post<
        ApiResponse<{ html: string; template_name: string; category: string }>
      >(`/document-templates/${id}/preview`, { data: data || {} });
      return response.data.data;
    },
  });
}

/**
 * Generates a document from a template.
 */
export function useGenerateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      template_id: string;
      document_type: string;
      data: Record<string, unknown>;
      file_name?: string;
      invoice_id?: string | null;
      client_id?: string | null;
      job_id?: string | null;
    }) => {
      const { data } = await apiClient.post<
        ApiResponse<{ document: GeneratedDocument; downloadUrl: string }>
      >('/document-templates/generate', input);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.generated() });
    },
  });
}

/**
 * Lists generated documents.
 */
export function useGeneratedDocuments(filters?: {
  document_type?: string;
  status?: string;
  invoice_id?: string;
  client_id?: string;
  page?: string;
  page_size?: string;
}) {
  return useQuery({
    queryKey: templateKeys.generated(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.set(key, value);
        });
      }
      const { data } = await apiClient.get<
        ApiResponse<GeneratedDocument[]> & {
          pagination: { page: number; pageSize: number; total: number; totalPages: number };
        }
      >(`/document-templates/generated?${params.toString()}`);
      return data;
    },
  });
}

/**
 * Gets a download URL for a generated document.
 */
export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.get<ApiResponse<{ downloadUrl: string; expiresIn: number }>>(
        `/document-templates/generated/${id}/download`,
      );
      return data.data;
    },
  });
}

// ─── Report Template Placeholders ────────────────────────────────────────────

/**
 * Builds the fixed report-level placeholders that every report template exposes,
 * regardless of the selected data source.
 */
export function reportBaseVariables(): TemplateVariable[] {
  return [
    { key: 'report.title', label: 'Report Title', type: 'string', source: 'report' },
    { key: 'report.type', label: 'Report Type', type: 'string', source: 'report' },
    { key: 'report.data_source', label: 'Data Source', type: 'string', source: 'report' },
    { key: 'report.generated_at', label: 'Generated At', type: 'date', source: 'report' },
    { key: 'report.total_rows', label: 'Total Rows', type: 'number', source: 'report' },
    { key: 'columns', label: 'Column Definitions (Array)', type: 'array', source: 'report' },
    { key: 'data', label: 'Report Rows (Array)', type: 'array', source: 'report' },
  ];
}

// ─── Report PDF Generation ────────────────────────────────────────────────────

/**
 * Generates a PDF for a saved report using its attached (or default) report template.
 * Returns the generated document + a presigned download URL.
 */
export function useGenerateReportPdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      savedReportId,
      templateId,
    }: {
      savedReportId: string;
      templateId?: string | null;
    }) => {
      const { data } = await apiClient.post<
        ApiResponse<{ document: GeneratedDocument; downloadUrl: string }>
      >(`/reports/saved/${savedReportId}/generate-pdf`, {
        template_id: templateId ?? null,
      });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.generated() });
    },
  });
}

/**
 * Renders a saved report's template to HTML for on-screen preview (no PDF).
 */
export function usePreviewReportPdf() {
  return useMutation({
    mutationFn: async ({
      savedReportId,
      templateId,
    }: {
      savedReportId: string;
      templateId?: string | null;
    }) => {
      const { data } = await apiClient.post<ApiResponse<{ html: string }>>(
        `/reports/saved/${savedReportId}/preview-pdf`,
        { template_id: templateId ?? null },
      );
      return data.data;
    },
  });
}
